import React, { FC, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, styled, Theme, Button as MuiButton } from '@material-ui/core';
import { useI18nPath } from '../../../config/clientRoutes/hooks';
import apiRoutes from '../../../config/api/routes';
import { meMapper } from '../../../config/api/models';
import {
  Button,
  InlineLink,
  Icons,
  CheckboxAdapter,
  FormControl,
  Message,
  validations,
} from '../../shared/components';
import { useFetch } from '../../shared/hooks';
import { format } from 'date-fns';
import { logOff } from 'src/modules/shared/slices/logout/slice';
import { useAppDispatch } from 'src/modules/shared/hooks/useAppDispatch';
import { persistor } from 'src/modules/shared/store';
import { setUser } from 'src/modules/shared/slices/auth/entity/AuthSlice';

const ContentWrapper = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ComplianceText = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const FormWrapper = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledFormControl = styled(FormControl)({
  marginBottom: 0,
});

const CookieCompliance: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { getPath } = useI18nPath();

  const [patchMeResponse, patchMe] = useFetch(apiRoutes.me(), {
    method: 'PATCH',
    mapper: meMapper,
  });

  useEffect(() => {
    if (patchMeResponse.success) {
      dispatch(setUser(patchMeResponse.data));
    }
  }, [dispatch, patchMeResponse]);

  const handleLogout = useCallback(async () => {
    dispatch(logOff());
    await persistor.purge();
    history.push(getPath('login'));
  }, [dispatch, history, getPath]);

  return (
    <>
      <ContentWrapper>
        <ComplianceText>
          {t('security.cookieCompliance.content.text').split('%termsofuse%')[0]}
          <InlineLink to="termsOfUse" rel="noopener noreferrer">
            {t('security.cookieCompliance.content.linkLabelTermsOfUse')}
          </InlineLink>
          {
            t('security.cookieCompliance.content.text')
              .split('%termsofuse%')[1]
              .split('%privacy%')[0]
          }
          <InlineLink to="privacyPolicy" target="_blank">
            {t('security.cookieCompliance.content.linkLabelPrivacy')}
          </InlineLink>
          {
            t('security.cookieCompliance.content.text')
              .split('%termsofuse%')[1]
              .split('%privacy%')[1]
          }
        </ComplianceText>
        <FormWrapper>
          <Form
            onSubmit={() =>
              patchMe({
                body: {
                  privacyAcceptedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                },
              })
            }
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <StyledFormControl id="consent-termsofuse" required>
                  <Field
                    name="consent-termsofuse"
                    component={CheckboxAdapter}
                    type="checkbox"
                    validate={validations(t).required}
                    label={t('security.cookieCompliance.content.consentTermsOfUse')}
                  />
                </StyledFormControl>
                <FormControl id="consent-privacy" required>
                  <Field
                    name="consent-privacy"
                    component={CheckboxAdapter}
                    type="checkbox"
                    validate={validations(t).required}
                    label={t('security.cookieCompliance.content.consentPrivacy')}
                  />
                </FormControl>
                <ButtonGroup>
                  <Button icon={Icons.CHECK} type="submit">
                    {t('security.cookieCompliance.acceptBtn')}
                  </Button>
                  <MuiButton variant="text" onClick={handleLogout}>
                    {t('security.cookieCompliance.toLoginBtn')}
                  </MuiButton>
                </ButtonGroup>
                {patchMeResponse && patchMeResponse.error && (
                  <Message type="error">{t(patchMeResponse.reason)}</Message>
                )}
              </form>
            )}
          />
        </FormWrapper>
      </ContentWrapper>
    </>
  );
};

export default CookieCompliance;
